import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Phone, ArrowRight, CheckCircle, MapPin, Clock, Shield, Star,
  AlertTriangle, Frame, Hammer, HelpCircle
} from 'lucide-react';
import { companyInfo, services, cities } from '../../data/mock';
import { getCityContent } from '../../data/cityContent';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList,
  BreadcrumbPage, BreadcrumbSeparator,
} from '../ui/breadcrumb';
import SEO, { getCitySeo } from '../seo/SEO';

const SITE = 'https://www.menuisiervitrier.fr';

const CityPage = () => {
  const { cityId } = useParams();
  const city = cities.find((c) => c.id === cityId);
  const content = getCityContent(cityId);
  const citySeo = getCitySeo(cityId);

  if (!city) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO title="Ville non trouvée | NGL SERVICES" description="Page non trouvée" noindex={true} />
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Ville non trouvée</h1>
          <Link to="/" className="text-[hsl(207,78%,42%)] hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  const dept = content?.department || 'Val-de-Marne (94)';
  const nearbyCities = (content?.nearby || [])
    .map((id) => cities.find((c) => c.id === id))
    .filter(Boolean);
  const otherCities = nearbyCities.length ? nearbyCities : cities.filter((c) => c.id !== cityId).slice(0, 4);

  // Lien rénovation fenêtres propre à la ville
  const renovationLink = cityId === 'villiers-sur-marne'
    ? '/renovation-fenetres-paiement-plusieurs-fois-villiers-sur-marne'
    : `/renovation-fenetres-${cityId}`;

  // ---- Schemas ----
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": `${companyInfo.name} - Vitrier à ${city.name}`,
    "description": `NGL SERVICES, vitrier, menuisier et serrurier à ${city.name} (${city.postalCode}). Remplacement vitre cassée, bris de glace, double vitrage, fenêtres, volets et serrurerie. Urgence 24h/24, devis gratuit.`,
    "telephone": companyInfo.phone,
    "url": `${SITE}/villes/${cityId}`,
    "areaServed": { "@type": "City", "name": city.name, "postalCode": city.postalCode },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "27 avenue André Rouy",
      "addressLocality": "Villiers-sur-Marne",
      "postalCode": "94350",
      "addressRegion": "Île-de-France",
      "addressCountry": "FR"
    },
    "geo": { "@type": "GeoCoordinates", "latitude": 48.8273, "longitude": 2.5449 },
    "openingHours": "Mo-Su 00:00-23:59",
    "priceRange": "€€",
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Vitrerie, menuiserie et serrurerie",
    "provider": { "@type": "LocalBusiness", "name": companyInfo.name, "telephone": companyInfo.phone },
    "areaServed": { "@type": "City", "name": city.name, "postalCode": city.postalCode },
    "description": `Services de vitrerie (remplacement vitre cassée, bris de glace, double vitrage), menuiserie (fenêtres, volets) et serrurerie à ${city.name}.`,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": SITE + "/" },
      { "@type": "ListItem", "position": 2, "name": "Zones d'intervention", "item": SITE + "/villes/villiers-sur-marne" },
      { "@type": "ListItem", "position": 3, "name": city.name, "item": `${SITE}/villes/${cityId}` },
    ],
  };

  const faqSchema = content?.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": content.faq.map((f) => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a },
        })),
      }
    : null;

  const additionalSchemas = [serviceSchema, breadcrumbSchema, faqSchema].filter(Boolean);

  return (
    <div className="min-h-screen">
      <SEO
        title={citySeo.title}
        description={citySeo.description}
        keywords={citySeo.keywords}
        canonical={`/villes/${cityId}`}
        schemaData={localBusinessSchema}
        additionalSchemas={additionalSchemas}
      />

      {/* Hero */}
      <section className="relative py-20 bg-[hsl(207,78%,42%)]">
        <div className="container mx-auto px-4 relative z-10">
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-white/70">
              <BreadcrumbItem><BreadcrumbLink asChild><Link to="/" className="hover:text-white">Accueil</Link></BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem><BreadcrumbLink asChild><Link to="/villes/villiers-sur-marne" className="hover:text-white">Zones d'intervention</Link></BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem><BreadcrumbPage className="text-white">{city.name}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-8 h-8 text-[hsl(24,95%,53%)]" />
              {city.isPrimary && <Badge className="bg-[hsl(24,95%,53%)] text-white">Siège social</Badge>}
              <Badge className="bg-white/15 text-white border-white/30">{dept}</Badge>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Vitrier à {city.name} ({city.postalCode})
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Vitrier, menuisier et serrurier à {city.name}. Remplacement de vitre cassée, bris de glace,
              double vitrage, fenêtres, volets et dépannage serrurerie. Urgence 24h/24 et 7j/7.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${companyInfo.phoneEmergency}`}>
                <Button size="lg" className="emergency-btn rounded-full">
                  <Phone className="w-5 h-5 mr-2" /> Urgence : {companyInfo.phoneEmergency}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full">
                  Devis gratuit à {city.name} <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro unique + quartiers */}
      {content && (
        <section className="py-14">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">
              Votre vitrier et menuisier de proximité à {city.name}
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">{content.intro}</p>
            <p className="text-muted-foreground leading-relaxed mb-6">{content.context}</p>
            {content.quartiers?.length > 0 && (
              <div className="bg-[hsl(210,20%,98%)] rounded-xl p-5">
                <h3 className="font-semibold text-[hsl(215,25%,15%)] mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[hsl(207,78%,42%)]" /> Quartiers desservis à {city.name}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {content.quartiers.map((q, i) => (
                    <span key={i} className="text-sm bg-white border rounded-full px-3 py-1 text-slate-700">{q}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Services */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-4">Nos services à {city.name}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Vitrerie, bris de glace, remplacement de vitre cassée, double vitrage, fenêtres, volets et serrurerie à {city.name} et ses environs.
            </p>
            <div className="section-divider" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link key={service.id} to={`/services/${service.id}`}>
                <Card className="service-card h-full cursor-pointer border-0 shadow-md">
                  <div className="relative h-44 overflow-hidden rounded-t-lg">
                    <img
                      src={service.image}
                      alt={`${service.title} à ${city.name} (${city.postalCode}) - NGL SERVICES`}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4"><h3 className="text-lg font-bold text-white">{service.title}</h3></div>
                  </div>
                  <CardContent className="p-5">
                    <p className="text-muted-foreground text-sm mb-3">{service.shortDesc} à {city.name}</p>
                    <ul className="space-y-2">
                      {service.features.slice(0, 2).map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-[hsl(207,78%,42%)]" /><span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Maillage interne : urgences & prestations phares */}
      <section className="py-14">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-[hsl(215,25%,15%)] mb-8 text-center">
            Interventions les plus demandées à {city.name}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {[
              { to: '/vitrier-urgence-villiers-sur-marne', icon: AlertTriangle, title: 'Vitrier urgence 24h/24', desc: `Dépannage rapide à ${city.name}` },
              { to: '/bris-de-glace-villiers-sur-marne', icon: Shield, title: 'Bris de glace', desc: 'Sécurisation et remplacement' },
              { to: '/remplacement-vitre-cassee-villiers-sur-marne', icon: Frame, title: 'Vitre cassée', desc: 'Remplacement tout vitrage' },
              { to: renovationLink, icon: Hammer, title: 'Rénovation fenêtres', desc: 'Paiement en plusieurs fois' },
            ].map((item, i) => (
              <Link key={i} to={item.to}>
                <Card className="h-full border-0 shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="p-5">
                    <div className="w-11 h-11 rounded-lg bg-[hsl(207,78%,95%)] flex items-center justify-center mb-3">
                      <item.icon className="w-6 h-6 text-[hsl(207,78%,42%)]" />
                    </div>
                    <h3 className="font-semibold text-[hsl(215,25%,15%)]">{item.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{item.desc}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="badge-primary mb-4">Votre artisan local</Badge>
              <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-6">Pourquoi choisir NGL SERVICES à {city.name} ?</h2>
              <div className="space-y-4">
                {[
                  { icon: MapPin, title: 'Proximité', desc: `Intervention rapide à ${city.name} et dans tout le ${dept}` },
                  { icon: Clock, title: 'Disponibilité 24h/24', desc: "Service d'urgence disponible jour et nuit, 7j/7" },
                  { icon: Shield, title: 'Travail garanti', desc: "Garantie sur l'ensemble de nos prestations" },
                  { icon: Star, title: 'Expertise locale', desc: `Connaissance des quartiers de ${city.name}` },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-white rounded-lg shadow-sm">
                    <div className="w-12 h-12 rounded-lg bg-[hsl(207,78%,95%)] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-[hsl(207,78%,42%)]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[hsl(215,25%,15%)]">{item.title}</h3>
                      <p className="text-muted-foreground text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Card className="border-0 shadow-xl bg-[hsl(207,78%,42%)] text-white">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-4">Contactez votre vitrier à {city.name}</h3>
                  <p className="text-white/80 mb-6">Devis gratuit et sans engagement pour tous vos travaux de vitrerie, menuiserie et serrurerie.</p>
                  <div className="space-y-4">
                    <a href={`tel:${companyInfo.phone}`} className="block">
                      <div className="flex items-center gap-3 p-4 bg-white/10 rounded-lg hover:bg-white/20 transition-colors">
                        <Phone className="w-6 h-6" />
                        <div><p className="text-sm text-white/70">Téléphone</p><p className="font-bold text-lg">{companyInfo.phone}</p></div>
                      </div>
                    </a>
                    <a href={`tel:${companyInfo.phoneEmergency}`} className="block">
                      <div className="flex items-center gap-3 p-4 bg-[hsl(24,95%,53%)] rounded-lg hover:bg-[hsl(24,95%,45%)] transition-colors">
                        <Clock className="w-6 h-6" />
                        <div><p className="text-sm text-white/90">Urgences 24h/24</p><p className="font-bold text-lg">{companyInfo.phoneEmergency}</p></div>
                      </div>
                    </a>
                    <Link to="/contact" className="block">
                      <Button className="w-full bg-white text-[hsl(207,78%,42%)] hover:bg-gray-100 rounded-full text-lg py-6">Demander un devis gratuit</Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ locale */}
      {content?.faq?.length > 0 && (
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[hsl(215,25%,15%)] mb-3">Questions fréquentes — {city.name}</h2>
              <div className="section-divider" />
            </div>
            <div className="space-y-4">
              {content.faq.map((f, i) => (
                <Card key={i} className="border-0 shadow-sm">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-[hsl(215,25%,15%)] flex items-start gap-2 mb-2">
                      <HelpCircle className="w-5 h-5 text-[hsl(207,78%,42%)] flex-shrink-0 mt-0.5" /> {f.q}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed pl-7">{f.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Villes proches (maillage) */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-8 text-center">Vitrier dans les villes proches de {city.name}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {otherCities.map((c) => (
              <Link key={c.id} to={`/villes/${c.id}`}>
                <div className="city-card text-center">
                  <MapPin className="w-5 h-5 mx-auto mb-2 text-[hsl(207,78%,42%)]" />
                  <p className="font-medium text-sm text-[hsl(215,25%,15%)]">Vitrier {c.name}</p>
                  <p className="text-xs text-muted-foreground">{c.postalCode}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/villes/villiers-sur-marne">
              <Button variant="outline" className="rounded-full">Voir toutes nos zones d'intervention</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[hsl(215,25%,15%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Besoin d'un vitrier à {city.name} ?</h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">NGL SERVICES intervient à {city.name} pour tous vos travaux de vitrerie, menuiserie et serrurerie. Devis gratuit et intervention rapide garantie.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="emergency-btn rounded-full"><Phone className="w-5 h-5 mr-2" />{companyInfo.phoneEmergency}</Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full">Demander un devis gratuit</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CityPage;
